/**
 * Nửa trên màn chơi: cảnh chiến đấu (hero + monster, HP, intent, choáng, mục tiêu), HUD
 * (tầng/wave/vòng, HP + khiên hero) và thanh skill (chuỗi orb cần cho 3 skill + ult, hồi chiêu,
 * thanh ult). Vẽ từ RunState của @ml/core; `play` diễn sự kiện của một bước rồi `sync` về state cuối.
 */
import {
  AnimatedSprite,
  Container,
  Graphics,
  Sprite,
  Text,
  TilingSprite,
  type Ticker,
} from 'pixi.js';
import {
  isPrefixOf,
  type BattleEvent,
  type MonsterState,
  type RunEvent,
  type RunState,
  type SeqToken,
} from '@ml/core';
import type { Color, Config, Content } from '@ml/config';
import type { Skin } from '../art/skin.ts';
import type { Layout } from '../board/layout.ts';
import { COLOR_HEX } from '../board/orbArt.ts';
import { easeOutQuad, Tweener } from '../board/tween.ts';
import { intentDamage, intentIcons } from './intent.ts';

const FONT = 'monospace';
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Đặt chữ vừa `maxW`: thu cỡ chữ tới 7px, còn tràn thì cắt đuôi bằng "…". */
function fitText(t: Text, text: string, maxW: number, size: number): void {
  if (t.text === text && t.style.fontSize === size && t.width <= maxW) return;
  t.style.fontSize = size;
  t.text = text;
  for (let s = size; t.width > maxW && s > 7; s--) t.style.fontSize = s - 1;
  if (t.width <= maxW) return;
  let n = text.length;
  while (n > 1 && t.width > maxW) t.text = `${text.slice(0, --n).trimEnd()}…`;
}

class CharNode {
  readonly root = new Container();
  readonly body: Container;
  private sprite: AnimatedSprite | null = null;
  readonly hpBar = new Graphics();
  readonly hpText = new Text({
    text: '',
    style: { fill: 0xffffff, fontSize: 10, fontFamily: FONT },
  });
  readonly extra = new Container();
  height = 48;
  width = 48;
  scale = 1;
  dead = false;
  /** Màu thanh HP (hero xanh như HUD, monster đỏ). */
  hpColor = 0xd83a3a;
  /** Bóng dưới chân (nằm ngoài `root`, mờ theo khi chết). */
  shadow: Container | null = null;

  constructor(
    private readonly skin: Skin | null,
    readonly artKey: string,
    placeholderColor: number,
    private readonly onFrame: () => void,
  ) {
    const idle = skin?.frames(`${artKey}.idle`) ?? [];
    if (idle.length > 0) {
      const s = new AnimatedSprite(idle);
      const [ax, ay] = skin!.anchor(`${artKey}.idle`);
      s.anchor.set(ax, ay);
      s.animationSpeed = skin!.fps(`${artKey}.idle`) / 60;
      s.onFrameChange = () => this.onFrame();
      s.play();
      this.sprite = s;
      this.body = s;
      this.width = idle[0]!.width;
      this.height = idle[0]!.height;
    } else {
      const g = new Graphics().rect(-20, -44, 40, 44).fill(placeholderColor);
      this.body = g;
      this.width = 40;
      this.height = 44;
    }
    this.root.addChild(this.body, this.hpBar, this.hpText, this.extra);
  }

  setScale(s: number): void {
    this.scale = s;
    this.body.scale.set(s);
  }

  /** Chạy anim một lần (attack/cast/hit/death), xong quay về idle (trừ death). */
  playOnce(anim: 'attack' | 'cast' | 'hit' | 'death', speed: number): Promise<void> {
    const sp = this.sprite;
    const frames = this.skin?.frames(`${this.artKey}.${anim}`) ?? [];
    if (!sp || frames.length === 0) {
      if (anim === 'death') this.root.alpha = 0.25;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const idle = this.skin!.frames(`${this.artKey}.idle`);
      sp.textures = frames;
      sp.loop = false;
      sp.animationSpeed = (this.skin!.fps(`${this.artKey}.${anim}`) / 60) * speed;
      const done = () => {
        clearTimeout(guard);
        if (anim !== 'death' && !this.dead) {
          sp.textures = idle;
          sp.loop = true;
          sp.animationSpeed = this.skin!.fps(`${this.artKey}.idle`) / 60;
          sp.play();
        }
        resolve();
      };
      // Chặn treo nếu ticker dừng (tab nền): tối đa 1.5 s.
      const guard = setTimeout(done, 1500);
      sp.onComplete = done;
      sp.gotoAndPlay(0);
    });
  }

  drawHp(hp: number, maxHp: number, shield = 0): void {
    const w = Math.max(40, Math.min(72, this.width * this.scale));
    const y = -this.height * this.scale - 12;
    const g = this.hpBar.clear();
    g.rect(-w / 2 - 1, y - 1, w + 2, 7).fill(0x000000);
    g.rect(-w / 2, y, w, 5).fill(0x3a1820);
    g.rect(-w / 2, y, Math.round((w * Math.max(0, hp)) / maxHp), 5).fill(this.hpColor);
    if (shield > 0) g.rect(-w / 2, y - 3, Math.min(w, (w * shield) / maxHp), 2).fill(0x7ac8ff);
    this.hpText.text = `${Math.max(0, hp)}`;
    this.hpText.anchor.set(0.5, 1);
    this.hpText.position.set(0, y - 2);
  }
}

type SlotNode = { root: Container; bg: Graphics; cd: Text; name: Text };

export class BattleView {
  readonly root = new Container();
  private readonly bg = new Container();
  private readonly actors = new Container();
  private readonly fx = new Container();
  private readonly hud = new Container();
  private readonly hudBg = new Graphics();
  private readonly hudText = new Text({
    text: '',
    style: { fill: 0xe8e8f0, fontSize: 13, fontFamily: FONT },
  });
  private readonly hpBar = new Graphics();
  private readonly hpText = new Text({
    text: '',
    style: { fill: 0xffffff, fontSize: 12, fontFamily: FONT },
  });
  private readonly buildText = new Text({
    text: '',
    style: { fill: 0x6a6a80, fontSize: 9, fontFamily: FONT },
  });
  private readonly bar = new Container();
  private slots: SlotNode[] = [];
  private hero: CharNode | null = null;
  private monsters: CharNode[] = [];
  private arrow: Container | null = null;
  private layout: Layout | null = null;
  private state: RunState | null = null;
  private readonly tweener: Tweener;
  private path: readonly SeqToken[] = [];
  /** Slot monster đang chọn làm mục tiêu (undefined = combat.defaultTarget). */
  target: number | undefined;
  dirty = true;
  onTarget?: (slot: number) => void;

  constructor(
    private readonly ticker: Ticker,
    private readonly skin: Skin | null,
    private readonly config: Config,
    private readonly content: Content,
    build: string,
  ) {
    this.tweener = new Tweener(ticker);
    this.tweener.speed = Math.max(0.1, config.ui.anim.speedPct / 100);
    this.buildText.text = build;
    this.hud.addChild(this.hudBg, this.hudText, this.hpBar, this.hpText, this.buildText);
    this.root.addChild(this.bg, this.actors, this.fx, this.hud, this.bar);
  }

  get busy(): boolean {
    return this.tweener.active > 0;
  }

  private get speed(): number {
    return this.tweener.speed;
  }

  private ms(base: number): number {
    return base / this.speed;
  }

  private markDirty = (): void => {
    this.dirty = true;
  };

  setLayout(l: Layout): void {
    this.layout = l;
    this.drawBackground(l);
    this.drawHudFrame(l);
    this.buildSkillBar(l);
    if (this.state) this.rebuildActors(this.state);
    this.dirty = true;
  }

  /** Chuỗi đang chọn (token màu) → sáng ô skill có dãy nhận chuỗi này làm tiền tố. */
  setSelection(seq: readonly SeqToken[]): void {
    this.path = seq;
    if (this.state) this.drawSkillBar(this.state);
  }

  // ---------------------------------------------------------------- nền
  private drawBackground(l: Layout): void {
    for (const c of this.bg.removeChildren()) c.destroy();
    const sceneBottom = l.scene.y + l.scene.height;
    const fill = new Graphics().rect(0, 0, l.viewport.width, sceneBottom).fill(0x15121f);
    this.bg.addChild(fill);
    const far = this.skin?.texture('bg.sunkenCrypt.far');
    if (far) {
      // Phủ kín từ mép trên màn (tràn sau Dynamic Island) đến đáy cảnh, phóng hệ số nguyên.
      const s = Math.max(
        1,
        Math.ceil(Math.max(l.viewport.width / far.width, sceneBottom / far.height)),
      );
      const sp = new Sprite(far);
      sp.scale.set(s);
      sp.position.set(
        Math.round((l.viewport.width - far.width * s) / 2),
        sceneBottom - far.height * s,
      );
      this.bg.addChild(sp);
    }
    const floor = this.skin?.texture('bg.sunkenCrypt.floor');
    const fh = Math.round(Math.min(48, l.scene.height * 0.2));
    if (floor) {
      const ts = new TilingSprite({ texture: floor, width: l.viewport.width, height: fh });
      ts.tileScale.set(fh / floor.height);
      ts.position.set(0, sceneBottom - fh);
      this.bg.addChild(ts);
    } else {
      this.bg.addChild(
        new Graphics().rect(0, sceneBottom - fh, l.viewport.width, fh).fill(0x2a2438),
      );
    }
    const mask = new Graphics().rect(0, 0, l.viewport.width, sceneBottom).fill(0xffffff);
    this.bg.addChild(mask);
    this.bg.mask = mask;
  }

  private drawHudFrame(l: Layout): void {
    this.hudBg
      .clear()
      .rect(l.hud.x, l.hud.y, l.hud.width, l.hud.height)
      .fill({ color: 0x000000, alpha: 0.45 });
    this.hudText.position.set(l.hud.x + 8, l.hud.y + 4);
    this.buildText.anchor.set(1, 0);
    this.buildText.position.set(l.hud.x + l.hud.width - 4, l.hud.y + 2);
  }

  private feetY(l: Layout): number {
    return l.scene.y + l.scene.height - Math.round(Math.min(48, l.scene.height * 0.2) * 0.45);
  }

  // ---------------------------------------------------------------- actors
  private rebuildActors(s: RunState): void {
    const l = this.layout;
    if (!l) return;
    for (const c of this.actors.removeChildren()) c.destroy({ children: true });
    const b = s.battle;
    const heroArt = b.hero.artKey ?? `hero.${b.hero.id}`;
    const hero = new CharNode(this.skin, heroArt, 0x4a6fb0, this.markDirty);
    hero.hpColor = 0x48c060;
    const feet = this.feetY(l);
    const maxH = l.scene.height * 0.55;
    hero.setScale(Math.min(l.orbScale, maxH / hero.height));
    hero.root.position.set(Math.round(l.scene.x + l.scene.width * 0.17), feet);
    this.actors.addChild(this.shadow(hero), hero.root);
    this.hero = hero;

    const n = b.monsters.length;
    const x0 = l.scene.x + l.scene.width * 0.4;
    const slotW = (l.scene.width * 0.58) / Math.max(1, n);
    this.monsters = b.monsters.map((m, i) => {
      const def = this.content.monsters.find((d) => d.id === m.id);
      const node = new CharNode(
        this.skin,
        def?.artKey ?? `monster.${m.id}`,
        0x7a3a4a,
        this.markDirty,
      );
      node.setScale(Math.min(l.orbScale, maxH / node.height, (slotW * 0.95) / node.width));
      node.root.position.set(Math.round(x0 + slotW * (i + 0.5)), feet);
      // Chạm monster → chọn mục tiêu.
      node.root.eventMode = 'static';
      node.root.cursor = 'pointer';
      node.root.hitArea = {
        contains: (x: number, y: number) =>
          Math.abs(x) < slotW / 2 && y < 0 && y > -node.height * node.scale - 30,
      };
      node.root.on('pointertap', () => this.onTarget?.(i));
      this.actors.addChild(this.shadow(node), node.root);
      if (m.hp <= 0) {
        node.dead = true;
        node.root.alpha = 0;
        if (node.shadow) node.shadow.alpha = 0;
      }
      return node;
    });
    const arrowTex = this.skin?.texture('ui.targetArrow');
    const arrow = new Container();
    if (arrowTex) {
      const a = new Sprite(arrowTex);
      a.anchor.set(0.5, 1);
      a.scale.set(2);
      arrow.addChild(a);
    } else arrow.addChild(new Graphics().poly([-6, -10, 6, -10, 0, 0]).fill(0xffe28a));
    this.actors.addChild(arrow);
    this.arrow = arrow;
    this.syncActors(s);
  }

  private shadow(node: CharNode): Container {
    const t = this.skin?.texture(node.height > 64 ? 'shadow.large' : 'shadow.small');
    const c = new Container();
    if (t) {
      const sp = new Sprite(t);
      sp.anchor.set(0.5);
      sp.scale.set(node.scale);
      c.addChild(sp);
    } else
      c.addChild(
        new Graphics()
          .ellipse(0, 0, 20 * node.scale, 5 * node.scale)
          .fill({ color: 0, alpha: 0.4 }),
      );
    c.position.copyFrom(node.root.position);
    node.shadow = c;
    return c;
  }

  private syncActors(s: RunState): void {
    const b = s.battle;
    this.hero?.drawHp(b.heroState.hp, b.heroState.maxHp, b.heroState.shield);
    b.monsters.forEach((m, i) => {
      const node = this.monsters[i];
      if (!node) return;
      node.drawHp(m.hp, m.maxHp);
      this.drawIntent(node, m);
    });
    // Mục tiêu: slot đang chọn nếu còn sống, không thì monster sống đầu tiên.
    const alive = b.monsters.map((m, i) => (m.hp > 0 ? i : -1)).filter((i) => i >= 0);
    if (this.target !== undefined && !alive.includes(this.target)) this.target = undefined;
    const t = this.target ?? alive[0];
    const node = t === undefined ? undefined : this.monsters[t];
    if (this.arrow) {
      this.arrow.visible = !!node && alive.length > 1;
      if (node) {
        this.arrow.position.set(node.root.x, node.root.y - node.height * node.scale - 40);
      }
    }
    this.dirty = true;
  }

  private drawIntent(node: CharNode, m: MonsterState): void {
    for (const c of node.extra.removeChildren()) c.destroy({ children: true });
    if (m.hp <= 0) return;
    const y = -node.height * node.scale - 30;
    const intent = m.intents[m.intentIndex];
    if (intent && this.config.ui.showIntent) {
      let x = -18;
      for (const key of intentIcons(intent)) {
        const tex = this.skin?.texture(`intent.${key}`);
        if (tex) {
          const sp = new Sprite(tex);
          sp.anchor.set(0.5);
          sp.scale.set(1.5);
          sp.position.set(x, y);
          node.extra.addChild(sp);
        } else {
          node.extra.addChild(new Graphics().circle(x, y, 7).fill(0xd8a040));
        }
        x += 18;
      }
      const dmg = intentDamage(m, intent);
      const label = `${m.countdown}${dmg > 0 ? ` ${dmg}` : ''}`;
      const t = new Text({
        text: label,
        style: {
          fill: m.countdown <= 1 ? 0xff7060 : 0xffe28a,
          fontSize: 11,
          fontFamily: FONT,
          fontWeight: 'bold',
        },
      });
      t.anchor.set(0, 0.5);
      t.position.set(x - 8, y);
      node.extra.addChild(t);
    }
    if (m.stun > 0) {
      const tex = this.skin?.texture('vfx.stun', 3);
      if (tex) {
        const sp = new Sprite(tex);
        sp.anchor.set(0.5);
        sp.position.set(0, -node.height * node.scale * 0.85);
        node.extra.addChild(sp);
      }
      const z = new Text({
        text: `Choáng ${m.stun}`,
        style: { fill: 0xfff070, fontSize: 10, fontFamily: FONT },
      });
      z.anchor.set(0.5);
      z.position.set(0, -node.height * node.scale * 0.5);
      node.extra.addChild(z);
    }
  }

  // ---------------------------------------------------------------- HUD + skill bar
  private drawHud(s: RunState): void {
    const l = this.layout;
    if (!l) return;
    const b = s.battle;
    this.hudText.text = `Tầng ${s.floor + 1}/${s.floorCount}  Wave ${b.wave + 1}/${b.waves.length}  Vòng ${b.round}`;
    const w = Math.min(200, l.hud.width * 0.45);
    const x = l.hud.x + l.hud.width - w - 8;
    const y = l.hud.y + l.hud.height - 14;
    const hs = b.heroState;
    const g = this.hpBar.clear();
    g.rect(x - 1, y - 1, w + 2, 10).fill(0x000000);
    g.rect(x, y, w, 8).fill(0x3a1820);
    g.rect(x, y, Math.round((w * hs.hp) / hs.maxHp), 8).fill(0x48c060);
    if (hs.shield > 0)
      g.rect(x, y, Math.min(w, Math.round((w * hs.shield) / hs.maxHp)), 3).fill(0x7ac8ff);
    this.hpText.text = `HP ${hs.hp}/${hs.maxHp}${hs.shield > 0 ? `  Khiên ${hs.shield}(${hs.shieldTurns})` : ''}`;
    this.hpText.position.set(l.hud.x + 8, y - 4);
  }

  private buildSkillBar(l: Layout): void {
    for (const c of this.bar.removeChildren()) c.destroy({ children: true });
    this.slots = [];
    const sb = l.skillBar;
    this.bar.addChild(new Graphics().rect(sb.x, sb.y, sb.width, sb.height).fill(0x181826));
    const n = 4;
    const gap = 6;
    const sw = (sb.width - 16 - gap * (n - 1)) / n;
    for (let i = 0; i < n; i++) {
      const root = new Container();
      root.position.set(sb.x + 8 + i * (sw + gap), sb.y + 4);
      const bg = new Graphics();
      const name = new Text({
        text: '',
        style: { fill: 0xd0d0e0, fontSize: 10, fontFamily: FONT },
      });
      name.position.set(4, 2);
      const cd = new Text({
        text: '',
        style: { fill: 0xffffff, fontSize: 16, fontFamily: FONT, fontWeight: 'bold' },
      });
      cd.anchor.set(0.5);
      cd.position.set(sw / 2, (sb.height - 8) / 2 + 4);
      root.addChild(bg, name, cd);
      this.bar.addChild(root);
      this.slots.push({ root, bg, cd, name });
    }
    if (this.state) this.drawSkillBar(this.state);
  }

  private drawSkillBar(s: RunState): void {
    const l = this.layout;
    if (!l) return;
    const b = s.battle;
    const hero = b.hero;
    const sb = l.skillBar;
    const gap = 6;
    const sw = (sb.width - 16 - gap * 3) / 4;
    const sh = sb.height - 8;
    const gaugeMax = this.config.combat.ult.gaugeMax;
    for (let i = 0; i < 4; i++) {
      const slot = this.slots[i];
      if (!slot) continue;
      const skill = i === 3 ? hero.ultimate : hero.skills[i];
      if (!skill) continue;
      const seq = skill.sequence as readonly Color[];
      const cd = i === 3 ? 0 : (b.heroState.cooldowns[i] ?? 0);
      const ready = i === 3 ? b.heroState.gauge >= gaugeMax : cd === 0;
      const lit = ready && this.path.length > 0 && isPrefixOf(this.path, seq);
      const g = slot.bg.clear();
      g.roundRect(0, 0, sw, sh, 6)
        .fill(lit ? 0x3a3460 : 0x22223a)
        .stroke({
          width: 2,
          color: lit ? 0xffe28a : i === 3 && ready ? 0xf0c8ff : 0x34344e,
        });
      fitText(slot.name, (i === 3 ? 'ULT ' : '') + skill.name, sw - 8, sw < 80 ? 9 : 10);
      // Dãy orb cần nối: chấm màu theo `sequence`, sáng phần đã khớp tiền tố.
      const orb = Math.max(6, Math.min(14, (sw - 8) / seq.length - 2));
      const y = 18 + orb / 2;
      const x0 = (sw - seq.length * (orb + 2)) / 2 + orb / 2;
      for (let k = 0; k < seq.length; k++) {
        const done = lit && k < this.path.length;
        const tex = this.skin?.texture(`orb.${seq[k]}.basic`);
        const cx = x0 + k * (orb + 2);
        if (tex) {
          // Dùng chung texture orb, vẽ bằng Graphics.texture để không tạo Sprite mỗi lần.
          g.texture(tex, 0xffffff, cx - orb / 2, y - orb / 2, orb, orb);
        } else g.circle(cx, y, orb / 2).fill(COLOR_HEX[seq[k]!]);
        if (done) g.rect(cx - orb / 2, y + orb / 2 + 1, orb, 2).fill(0xffe28a);
      }
      if (i === 3) {
        const w = sw - 10;
        g.rect(5, sh - 9, w, 5).fill(0x101018);
        g.rect(
          5,
          sh - 9,
          Math.round((w * Math.min(b.heroState.gauge, gaugeMax)) / gaugeMax),
          5,
        ).fill(ready ? 0xf0c8ff : 0x9b59d0);
      }
      if (!ready && i !== 3) g.roundRect(0, 0, sw, sh, 6).fill({ color: 0x000000, alpha: 0.55 });
      slot.cd.text = !ready && i !== 3 ? `${cd}` : '';
      slot.cd.position.set(sw / 2, sh / 2 + 6);
    }
    this.dirty = true;
  }

  // ---------------------------------------------------------------- state
  /** Vẽ lại toàn bộ theo state (tầng mới / chơi lại / sau khi diễn xong). */
  sync(s: RunState, rebuild = false): void {
    const prev = this.state;
    this.state = s;
    const waveChanged = !prev || prev.floor !== s.floor || prev.battle.wave !== s.battle.wave;
    if (rebuild || waveChanged || this.monsters.length !== s.battle.monsters.length) {
      this.target = undefined;
      this.rebuildActors(s);
    } else this.syncActors(s);
    this.drawHud(s);
    this.drawSkillBar(s);
  }

  // ---------------------------------------------------------------- anim
  private popup(x: number, y: number, text: string, color: number, big = false): void {
    const t = new Text({
      text,
      style: {
        fill: color,
        fontSize: big ? 20 : 15,
        fontFamily: FONT,
        fontWeight: 'bold',
        stroke: { color: 0x000000, width: 3 },
      },
    });
    t.anchor.set(0.5);
    t.position.set(x, y);
    this.fx.addChild(t);
    void this.tweener
      .to(700, (p) => {
        t.y = y - 28 * easeOutQuad(p);
        t.alpha = p < 0.7 ? 1 : 1 - (p - 0.7) / 0.3;
      })
      .then(() => t.destroy());
  }

  private headOf(node: CharNode): { x: number; y: number } {
    return { x: node.root.x, y: node.root.y - node.height * node.scale * 0.6 };
  }

  /**
   * Diễn sự kiện của một bước lượt chạy, từ state `before` sang `after`. Trả về khi xong.
   * HP monster cập nhật dần theo phần sát thương; cuối cùng `sync(after)`.
   */
  async play(events: readonly RunEvent[], before: RunState, after: RunState): Promise<void> {
    const actionMs = this.config.ui.anim.monsterActionMs;
    const hp = before.battle.monsters.map((m) => m.hp);
    const battleEvents: BattleEvent[] = [];
    for (const e of events) if (e.type === 'battle') battleEvents.push(...e.events);
    let waveSwitched = false;
    for (const e of battleEvents) {
      switch (e.type) {
        case 'player': {
          const casting = e.action === 'ABILITY' || e.action === 'ULTIMATE';
          const heroAnim = this.hero?.playOnce(casting ? 'cast' : 'attack', this.speed);
          if (casting && e.slot !== undefined) {
            const sk =
              e.slot === 3 ? before.battle.hero.ultimate : before.battle.hero.skills[e.slot];
            if (sk && this.hero) {
              const h = this.headOf(this.hero);
              this.popup(h.x, h.y - 20, sk.name, 0xf0c8ff);
            }
          }
          const deaths: Promise<void>[] = [];
          for (const r of e.events) {
            if (r.type !== 'damage' || !r.part.applied) continue;
            const node = this.monsters[r.part.target];
            if (!node || waveSwitched) continue;
            hp[r.part.target] = Math.max(0, (hp[r.part.target] ?? 0) - r.part.value);
            const m = before.battle.monsters[r.part.target]!;
            node.drawHp(hp[r.part.target]!, m.maxHp);
            const h = this.headOf(node);
            this.popup(
              h.x + (Math.random() - 0.5) * 16,
              h.y,
              `${r.part.value}`,
              e.crit ? 0xffe060 : 0xffffff,
              e.crit,
            );
            if (hp[r.part.target]! <= 0 && !node.dead) {
              node.dead = true;
              for (const c of node.extra.removeChildren()) c.destroy({ children: true });
              node.hpBar.clear();
              node.hpText.text = '';
              deaths.push(node.playOnce('death', this.speed).then(() => this.fade(node)));
            } else void node.playOnce('hit', this.speed);
          }
          this.dirty = true;
          await Promise.all([heroAnim, wait(this.ms(actionMs * 0.6))]);
          await Promise.all(deaths);
          break;
        }
        case 'heroHeal':
          if (this.hero) {
            const h = this.headOf(this.hero);
            this.popup(h.x, h.y, `+${e.amount}`, 0x60e080);
            this.hero.drawHp(e.heroHp, before.battle.heroState.maxHp);
          }
          break;
        case 'heroShield':
          if (this.hero) {
            const h = this.headOf(this.hero);
            this.popup(h.x, h.y - 14, `Khiên ${e.amount}`, 0x7ac8ff);
          }
          break;
        case 'stun': {
          const node = this.monsters[e.slot];
          if (node && !waveSwitched) {
            const h = this.headOf(node);
            this.popup(h.x, h.y - 16, 'Choáng', 0xfff070);
          }
          break;
        }
        case 'waveStart':
          await wait(this.ms(actionMs * 0.5));
          waveSwitched = true;
          this.state = after;
          this.target = undefined;
          this.rebuildActors(after);
          for (const n of this.monsters) {
            n.root.alpha = 0;
            void this.tweener.to(300, (p) => (n.root.alpha = p));
          }
          await wait(this.ms(300));
          break;
        case 'monsterAttack': {
          const node = this.monsters[e.slot];
          if (node) await node.playOnce('attack', this.speed);
          if (this.hero) {
            const h = this.headOf(this.hero);
            this.popup(h.x, h.y, `-${e.damage}`, 0xff6050);
            if (e.absorbed > 0) this.popup(h.x, h.y - 18, `chặn ${e.absorbed}`, 0x7ac8ff);
            this.hero.drawHp(e.heroHp, before.battle.heroState.maxHp);
            void this.hero.playOnce(e.heroHp <= 0 ? 'death' : 'hit', this.speed);
          }
          this.dirty = true;
          await wait(this.ms(actionMs * 0.5));
          break;
        }
        case 'boardDebuff': {
          const node = this.monsters[e.slot];
          if (node) {
            const anim = node.playOnce('attack', this.speed);
            const h = this.headOf(node);
            const label: Record<string, string> = {
              lockOrbs: 'Khoá ngọc',
              freezeOrbs: 'Đóng băng',
              deadMark: 'Ấn tử',
              destroyOrbs: 'Phá ngọc',
            };
            this.popup(h.x, h.y - 16, label[e.effect] ?? e.effect, 0xd0b0ff);
            await anim;
          }
          await wait(this.ms(actionMs * 0.4));
          break;
        }
        case 'monsterHeal': {
          const node = this.monsters[e.slot];
          if (node) {
            const h = this.headOf(node);
            this.popup(h.x, h.y, `+${e.amount}`, 0x60e080);
          }
          break;
        }
        case 'deadMarkExplode':
          if (this.hero) {
            const h = this.headOf(this.hero);
            this.popup(h.x, h.y, `-${e.damage}`, 0xc04060);
            this.hero.drawHp(e.heroHp, before.battle.heroState.maxHp);
          }
          await wait(this.ms(actionMs * 0.3));
          break;
        default:
          break;
      }
    }
    this.sync(after);
  }

  private fade(node: CharNode): Promise<void> {
    const a0 = node.root.alpha;
    return this.tweener.to(250, (p) => {
      node.root.alpha = a0 * (1 - p);
      if (node.shadow) node.shadow.alpha = 1 - p;
    });
  }
}
